import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { CommonAccountsEnums } from "@/apis/enums";
import { SlcApplicationApis } from "@/apis/SlcApplicationApis";
import { isAccessTokenExpired } from "@/workers/accessTokenExpiry";

const SESSION_KEY = "common-accounts-master-session";

export interface MasterSessionUser {
    id: string;
    name: string;
    email: string;
    type: number;
}

export interface MasterSession {
    accessToken: string;
    refreshToken: string;
    user: MasterSessionUser;
}

const readSession = (): MasterSession | null => {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as MasterSession;
        const isMaster =
            parsed.user?.type === CommonAccountsEnums.UserType.Master && !!parsed.accessToken;
        if (!isMaster) {
            localStorage.removeItem(SESSION_KEY);
            return null;
        }

        return parsed;
    } catch {
        localStorage.removeItem(SESSION_KEY);
        return null;
    }
};

const slcApplicationApis = new SlcApplicationApis();
let refreshingSession: Promise<boolean> | null = null;

export const useAuthStore = defineStore("auth", () => {
    const initialSession = readSession();
    const accessToken = ref(initialSession?.accessToken ?? "");
    const refreshToken = ref(initialSession?.refreshToken ?? "");
    const user = ref<MasterSessionUser | null>(initialSession?.user ?? null);

    const isMaster = computed(
        () => user.value?.type === CommonAccountsEnums.UserType.Master && accessToken.value !== "",
    );

    const persistSession = () => {
        if (!isMaster.value) {
            localStorage.removeItem(SESSION_KEY);
            return;
        }

        const session: MasterSession = {
            accessToken: accessToken.value,
            refreshToken: refreshToken.value,
            user: user.value as MasterSessionUser,
        };
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    };

    const setSession = (session: MasterSession, persistRefreshToken: boolean) => {
        if (session.user.type !== CommonAccountsEnums.UserType.Master || !session.accessToken)
            return;

        accessToken.value = session.accessToken;
        refreshToken.value = persistRefreshToken ? session.refreshToken : "";
        user.value = session.user;
        persistSession();
    };

    const clearSession = () => {
        accessToken.value = "";
        refreshToken.value = "";
        user.value = null;
        localStorage.removeItem(SESSION_KEY);
    };

    const refreshSession = () => {
        if (!refreshToken.value) return Promise.resolve(false);
        if (refreshingSession) return refreshingSession;

        const currentRefreshToken = refreshToken.value;
        refreshingSession = slcApplicationApis
            .postRefreshToken(currentRefreshToken)
            .then((response) => {
                if (!response.accessToken || response.errorCode) {
                    clearSession();
                    return false;
                }

                accessToken.value = response.accessToken;
                if (response.refreshToken) refreshToken.value = response.refreshToken;
                persistSession();
                return isMaster.value;
            })
            .catch(() => {
                clearSession();
                return false;
            })
            .finally(() => {
                refreshingSession = null;
            });

        return refreshingSession;
    };

    const ensureMasterSession = async () => {
        if (isMaster.value && !isAccessTokenExpired(accessToken.value)) return true;
        if (!refreshToken.value) {
            clearSession();
            return false;
        }

        return refreshSession();
    };

    return {
        accessToken,
        refreshToken,
        user,
        isMaster,
        setSession,
        clearSession,
        refreshSession,
        ensureMasterSession,
    };
});
